/**
 * Shared helpers for building and detecting slim cache objects.
 * Slim caches are preview-only metadata - never returned directly to UI.
 */

/**
 * Check if cached data is a slim cache (preview-only)
 */
export function isSlimCache(data: any): boolean {
  return data && data._slim_cache === true;
}

/**
 * Check if Lighthouse cache is incomplete (missing key fields)
 * This catches cases where _slim_cache flag wasn't set but data is partial.
 * Accepts EITHER metrics object OR LighthouseReport *_value fields as valid.
 */
export function isIncompleteLighthouseCache(data: any): boolean {
  if (!data) return true;

  // Must have both mobile and desktop
  if (!data.mobile || !data.desktop) return true;

  // Helper to check if device has valid metrics in either format
  const hasValidDeviceMetrics = (device: any): boolean => {
    if (!device) return false;

    // Format 1: metrics object (slim cache style)
    const hasMetricsObj = device.metrics &&
      typeof device.metrics.lcp === 'number' &&
      typeof device.metrics.cls === 'number' &&
      (typeof device.metrics.fid === 'number' || typeof device.metrics.inp === 'number');

    // Format 2: *_value fields (LighthouseReport style) or direct lcp/cls/fid
    const hasValueFields =
      (typeof device.lcp_value === 'number' || typeof device.lcp === 'number') &&
      (typeof device.cls_value === 'number' || typeof device.cls === 'number') &&
      (typeof device.fid_value === 'number' || typeof device.fid === 'number' ||
       typeof device.inp_value === 'number' || typeof device.inp === 'number');

    return hasMetricsObj || hasValueFields;
  };

  // Only incomplete if BOTH formats are missing for either device
  if (!hasValidDeviceMetrics(data.mobile) || !hasValidDeviceMetrics(data.desktop)) return true;

  return false;
}

/**
 * Build a slim SEO cache object from a full analysis
 */
export function buildSlimSeoCache(analysis: any): any {
  if (!analysis) return null;

  return {
    id: analysis.id,
    website_url: analysis.website_url || analysis.url,
    created_at: analysis.created_at,
    overall_score: analysis.overall_score,
    performance_score: analysis.performance_score,
    accessibility_score: analysis.accessibility_score,
    best_practices_score: analysis.best_practices_score,
    seo_score: analysis.seo_score,
    page_title: analysis.page_title,
    meta_description: analysis.meta_description,
    canonical_url: analysis.canonical_url,
    images_count: analysis.images_count,
    images_missing_alt: analysis.images_missing_alt,
    internal_links: analysis.internal_links,
    external_links: analysis.external_links,
    issues_summary: analysis.issues_summary || {
      critical: 0,
      warning: 0,
      info: 0
    },
    _slim_cache: true
  };
}

/**
 * Build a slim Lighthouse cache object from analysis data
 */
export function buildSlimLighthouseCache(params: {
  normalizedUrl: string;
  data: any;
  analysisId?: string;
}): any {
  const { normalizedUrl, data, analysisId } = params;

  if (!data) return null;

  const extractMetrics = (deviceData: any) => {
    if (!deviceData) return null;

    // Build metrics with fallback chain: metrics object → direct fields → audits
    let metrics: any = undefined;

    if (deviceData.metrics) {
      // Use existing metrics object
      metrics = {
        lcp: deviceData.metrics.lcp,
        cls: deviceData.metrics.cls,
        fid: deviceData.metrics.fid,
        inp: deviceData.metrics.inp,
        fcp: deviceData.metrics.fcp,
        ttfb: deviceData.metrics.ttfb
      };
    } else {
      // Fallback to direct fields or audits
      metrics = {
        lcp: deviceData.lcp ?? deviceData.lcp_value ?? deviceData.audits?.['largest-contentful-paint']?.numericValue,
        cls: deviceData.cls ?? deviceData.cls_value ?? deviceData.audits?.['cumulative-layout-shift']?.numericValue,
        fid: deviceData.fid ?? deviceData.fid_value ?? deviceData.audits?.['max-potential-fid']?.numericValue,
        inp: deviceData.inp ?? deviceData.inp_value,
        fcp: deviceData.fcp ?? deviceData.fcp_value ?? deviceData.audits?.['first-contentful-paint']?.numericValue,
        ttfb: deviceData.ttfb ?? deviceData.ttfb_value ?? deviceData.audits?.['server-response-time']?.numericValue
      };
    }

    return {
      analysis_id: analysisId || deviceData.analysis_id,
      performance_score: deviceData.performance_score ?? deviceData.categories?.performance?.score,
      accessibility_score: deviceData.accessibility_score ?? deviceData.categories?.accessibility?.score,
      best_practices_score: deviceData.best_practices_score ?? deviceData.categories?.['best-practices']?.score,
      seo_score: deviceData.seo_score ?? deviceData.categories?.seo?.score,
      metrics
    };
  };

  return {
    analysis_id: analysisId || data.analysis_id,
    url: normalizedUrl,
    created_at: data.created_at || new Date().toISOString(),
    mobile: extractMetrics(data.mobile),
    desktop: extractMetrics(data.desktop),
    _slim_cache: true
  };
}
